// Reusable upload-policy helpers driven by Admin Config's upload settings
// (allowed file types + max count + max size). Build a policy once from the
// configured data, then reuse its `validateFile` / `isAllowedType` validators
// — the same way a compiled regex is reused — across any upload UI.
import type { UploadConfig } from "services/AdminConfig/adminConfigService";

// Map an admin-configured format NAME (e.g. "JPG") to the MIME types / file
// extensions it covers, so we can build the <input accept> list and validate a
// picked file by either its MIME type or its filename extension.
export const UPLOAD_FORMAT_SPECS: Record<string, string[]> = {
  JPG: ["image/jpeg", "image/jpg", ".jpg", ".jpeg"],
  JPEG: ["image/jpeg", "image/jpg", ".jpg", ".jpeg"],
  JFIF: ["image/jfif", ".jfif"],
  PNG: ["image/png", ".png"],
  BMP: ["image/bmp", ".bmp"],
  TIFF: ["image/tiff", ".tif", ".tiff"],
  GIF: ["image/gif", ".gif"],
  WEBP: ["image/webp", ".webp"],
  PDF: ["application/pdf", ".pdf"],
  DOC: ["application/msword", ".doc"],
  DOCX: [
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ".docx",
  ],
  XLS: ["application/vnd.ms-excel", ".xls"],
  XLSX: [
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ".xlsx",
  ],
};

/** MIME types + extensions that a configured format name maps to. */
export const specForFormat = (type: string): string[] =>
  UPLOAD_FORMAT_SPECS[type.trim().toUpperCase()] ?? [`.${type.trim().toLowerCase()}`];

export interface UploadPolicyInput {
  allowedFileTypes?: string[];
  maxFiles?: number;
  maxFileSizeKb?: number;
  maxFileSizeMb?: number;
}

export interface UploadPolicy {
  allowedTypes: string[];
  allowedTypesLabel: string;
  maxFiles: number;
  maxFileSizeKb: number;
  maxFileSizeMb: number;
  maxFileSizeBytes: number;
  /** Value for the file <input>'s `accept` attribute. */
  accept: string;
  /** Human-readable rules, e.g. "JPG, PDF, PNG · up to 3 files · max 2 MB each". */
  helpText: string;
  /** True when the file's type matches the configured allowed formats. */
  isAllowedType: (file: File) => boolean;
  /** True when the file is within the configured max size. */
  isWithinSize: (file: File) => boolean;
  /** Returns an error message for a single file, or "" when valid. */
  validateFile: (file: File) => string;
}

/**
 * Build a reusable upload policy from Admin Config data. The returned
 * `validateFile(file)` (and `isAllowedType` / `isWithinSize`) are bound to the
 * given config, so callers validate every picked file against the dynamic rules
 * without re-implementing the checks. Missing/zero limits mean "no restriction".
 */
export const buildUploadPolicy = (config: UploadPolicyInput): UploadPolicy => {
  const allowedTypes = config.allowedFileTypes?.length ? config.allowedFileTypes : [];
  const maxFiles = config.maxFiles && config.maxFiles > 0 ? config.maxFiles : 0;
  const maxFileSizeKb = config.maxFileSizeKb && config.maxFileSizeKb > 0 ? config.maxFileSizeKb : 0;
  const maxFileSizeBytes = maxFileSizeKb * 1024;
  const maxFileSizeMb =
    config.maxFileSizeMb && config.maxFileSizeMb > 0
      ? config.maxFileSizeMb
      : Number((maxFileSizeKb / 1024).toFixed(2));

  const accept = Array.from(new Set(allowedTypes.flatMap(specForFormat))).join(",");
  const allowedTypesLabel = allowedTypes.map((t) => t.toUpperCase()).join(", ");
  const helpText = [
    allowedTypesLabel || "Any file",
    maxFiles ? `up to ${maxFiles} file${maxFiles === 1 ? "" : "s"}` : null,
    maxFileSizeMb ? `max ${maxFileSizeMb} MB each` : null,
  ]
    .filter(Boolean)
    .join(" · ");

  // Split the configured formats into their extension and MIME specs once.
  const allowedSpecs = allowedTypes.flatMap(specForFormat);
  const allowedExtensions = allowedSpecs.filter((s) => s.startsWith("."));
  const allowedMimes = allowedSpecs.filter((s) => !s.startsWith("."));

  const isAllowedType = (file: File): boolean => {
    if (!allowedTypes.length) return true; // no type restriction configured
    const name = (file.name || "").toLowerCase();
    const mime = (file.type || "").toLowerCase();
    const dotIndex = name.lastIndexOf(".");
    const ext = dotIndex >= 0 ? name.slice(dotIndex) : "";

    // Match by file EXTENSION first — it's the reliable signal and the only way
    // to reject look-alike formats that share a MIME type. A `.jfif` file, for
    // example, is reported by the browser as `image/jpeg`, so a MIME-based check
    // would wrongly accept it when only JPG/PDF/PNG are configured.
    if (ext) return allowedExtensions.includes(ext);

    // No extension on the file name → fall back to the MIME type.
    return allowedMimes.includes(mime);
  };

  const isWithinSize = (file: File): boolean =>
    !maxFileSizeBytes || file.size <= maxFileSizeBytes;

  const validateFile = (file: File): string => {
    if (!isAllowedType(file)) {
      return `Only ${allowedTypesLabel} files are allowed.`;
    }
    if (!isWithinSize(file)) {
      return `${file.name}: exceeds the ${maxFileSizeMb} MB limit.`;
    }
    return "";
  };

  return {
    allowedTypes,
    allowedTypesLabel,
    maxFiles,
    maxFileSizeKb,
    maxFileSizeMb,
    maxFileSizeBytes,
    accept,
    helpText,
    isAllowedType,
    isWithinSize,
    validateFile,
  };
};

export type { UploadConfig };
